import { useRouter } from 'next/router'
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import debounce from 'lodash.debounce'
import getConfig from 'next/config'

import { useFetchAllCities } from '@/service/apiHooks/useFetchAllCities'
import { useFetchPaginationParkings } from '@/service/apiHooks/useFetchPaginationParkings'
import { useMarket } from '@/utils/multi-markets/context'
import { first } from '@/utils/common'
import { Parking } from '@/service'
import { getParkingLabel } from '@/utils/getParkingLabel'
import normalizeString from '@/utils/normalizeStringCharacters'

import useGeocodingService from './useGeocodingService'

interface Option {
  value: string
  label: string
  parking?: Parking
  address?: string
  city?: string
  feature_type?: string
  coordinates?: [number, number]
}

const {
  publicRuntimeConfig: { MAP_BOX_KEY }
} = getConfig()

export function useSearchBar(t: (key: string, options?: any) => string) {
  const router = useRouter()
  const { locale, market } = useMarket()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery)
  const { cities } = useFetchAllCities({
    params: { lang: locale, countryCode: market }
  })
  const { parkings, fetchNextPage, refetch } = useFetchPaginationParkings({
    params: {
      lang: locale,
      size: 10,
      countryCode: market,
      sorting: 'distance;asc',
      page: 0,
      ...(debouncedSearchQuery && { search: debouncedSearchQuery })
    }
  })
  const [activeOption, setActiveOption] = useState<Option>()
  const [locating, setLocating] = useState(false)
  const [locatingError, setLocatingError] = useState(false)
  const selectRef = useRef<any>(null)
  const geocodingService = useGeocodingService()

  const allParkings =
    parkings?.map((p: Parking) => ({
      value: getParkingLabel(p, t),
      label: getParkingLabel(p, t),
      parking: p
    })) || []

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 500)
    handler()
    return () => {
      handler.cancel()
    }
  }, [searchQuery])

  useEffect(() => {
    const { noResults } = router.query
    const value = first(noResults)
    if (value) setActiveOption({ label: value, value })
  }, [router.query])

  function submit(evt: FormEvent) {
    evt.preventDefault()
    const city = activeOption?.value
    const parking = activeOption?.parking
    if (parking) {
      router.push({
        pathname: '/parkings/[city]/[slug]',
        query: { city: parking.cityUrlSlug, slug: parking.urlSlug }
      })
    } else if (city) {
      router.push({ pathname: '/parkings/[city]', query: { city } })
    } else {
      selectRef.current?.focus()
    }
  }

  const loadMoreOptions = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  async function handleLoadMore() {
    loadMoreOptions()
  }

  function handleInputChange(inputValue: string) {
    setSearchQuery(inputValue)
  }

  function handleChange(option: any) {
    if (option?.parking) {
      router.push({
        pathname: `/parkings/[city]/[slug]`,
        query: {
          city: option.parking.cityUrlSlug,
          slug: option.parking.urlSlug
        }
      })
    } else if (option) {
      if (option.value) {
        router.push({
          pathname: '/parkings/[city]',
          query: { city: normalizeString(option.value).toLowerCase() }
        })
      } else {
        router.push({
          pathname: '/parkings',
          query: { noResults: option.label }
        })
      }
    }
    if (option) setActiveOption(option)
  }

  function noOptionsMessage({ inputValue }: { inputValue: string }) {
    if (inputValue) return t('common:search.noOptions')
    return null
  }

  function removeDuplicates(options: Option[]) {
    const seen = new Set<string>()
    const arr: Option[] = []
    for (const opt of options) {
      if (!seen.has(opt.value)) {
        seen.add(opt.value)
        arr.push(opt)
      }
    }
    return arr
  }

  async function fetchGeocodingResults(query: string): Promise<Option[]> {
    if (!query || query.length < 2 || !geocodingService.current) return []
    try {
      const response = await geocodingService.current
        .forwardGeocode({
          query,
          countries: ['pl'],
          language: ['pl'],
          limit: 10,
          types: ['place']
        })
        .send()

      const features = response.body.features
      return features.map(feature => {
        const name = feature.text || ''
        return {
          value: name,
          label: name,
          address: name,
          feature_type: 'place',
          coordinates: feature.center as [number, number]
        }
      })
    } catch (error) {
      return []
    }
  }

  async function localize(lnglat: [number, number]) {
    if (locating || !geocodingService.current) return
    setLocating(true)
    setLocatingError(false)

    try {
      const [longitude, latitude] = lnglat

      const response = await geocodingService.current
        .reverseGeocode({
          query: [longitude, latitude],
          countries: ['pl'],
          language: ['pl'],
          types: ['place'],
          limit: 1
        })
        .send()

      const feature = response.body.features[0]

      if (feature?.place_type?.[0] === 'place' && feature.text) {
        const city = feature.text
        const cityObj = cities?.items?.find(
          i =>
            i.label &&
            normalizeString(i.label.toLowerCase()) ===
              normalizeString(city.toLowerCase())
        )

        if (cityObj) {
          setActiveOption({
            value: cityObj?.cityUrlSlug || city,
            label: city,
            feature_type: 'place',
            coordinates: feature.center as [number, number]
          })
        } else {
          setLocatingError(true)
        }
      } else {
        setLocatingError(true)
      }
    } catch (error) {
      setLocatingError(true)
    } finally {
      setLocating(false)
    }
  }

  async function fetchOptions(inputValue: string): Promise<Option[]> {
    if (inputValue.length < 2) return []
    try {
      await refetch()
      const geocodeResults = await fetchGeocodingResults(inputValue)
      const parkOps =
        parkings?.map(p => ({
          value: getParkingLabel(p, t),
          label: getParkingLabel(p, t),
          parking: p
        })) || []
      const merged = [...geocodeResults, ...parkOps]
      return removeDuplicates(merged)
    } catch {
      return []
    }
  }

  function loadOptions(
    inputValue: string,
    callback: (options: Option[] | []) => void
  ) {
    debounce(async () => {
      const opts = await fetchOptions(inputValue)
      callback(opts)
    }, 500)()
  }

  return {
    cities,
    activeOption,
    loadOptions,
    allParkings,
    selectRef,
    submit,
    locating,
    localize,
    locatingError,
    noOptionsMessage,
    handleChange,
    handleLoadMore,
    handleInputChange
  }
}
